import { allCourses, getCourseBySlug, getCourseFaqs, getCoursePath } from '../data/courses';

/** Update SITE_URL if the final production domain changes. */
export const SITE_URL = 'https://www.bharattraining.com';
export const SITE_NAME = 'Bharat Industrial Training';
export const DEFAULT_OG_IMAGE = '/og-image.png';

const DEFAULT_KEYWORDS =
  'PLC training Noida, SCADA course Noida, industrial automation training, Bharat Training, industrial training institute Noida, SAP course Noida, full stack developer course, placement training Noida, Sector 3 Noida';

const ADDRESS = {
  '@type': 'PostalAddress',
  streetAddress: 'Ground Floor, E-25, Sector-3, Opp. Shiv Mandir E Block',
  addressLocality: 'Noida',
  addressRegion: 'Uttar Pradesh',
  postalCode: '201301',
  addressCountry: 'IN',
};

const PLACE = {
  '@type': 'Place',
  name: SITE_NAME,
  address: ADDRESS,
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 28.5858,
    longitude: 77.3133,
  },
};

export const PAGE_SEO = {
  '/': {
    title: 'Bharat Training | PLC, SCADA & Industrial Automation Institute in Noida',
    description:
      "Bharat Industrial Training is Noida's practical institute for PLC, SCADA, industrial automation, SAP and IT courses with real labs, expert mentors and placement support.",
    keywords: DEFAULT_KEYWORDS,
    path: '/',
  },
  '/courses': {
    title: 'Job Oriented Courses in Noida | PLC, SCADA, SAP, Data Science',
    description:
      'Explore job-oriented courses in Noida: PLC SCADA, Industrial Automation, SAP, Full Stack, Data Science, Digital Marketing, Tally, GST and more with placement support.',
    keywords:
      'PLC SCADA course fees Noida, industrial courses list, automation training programs, SAP training Noida, data science course Noida, job oriented courses Noida',
    path: '/courses',
  },
  '/about': {
    title: 'About Bharat Industrial Training Institute Noida',
    description:
      "Learn about Bharat Training, Noida's practical industrial training center with real PLC panels, certified trainers, career counselling and 500+ student success stories.",
    keywords: 'about Bharat Training, industrial training institute Noida, automation training center',
    path: '/about',
  },
  '/placements': {
    title: 'Placements & Success Stories | Bharat Training Noida',
    description:
      'See Bharat Training placement support, student success stories, hiring partners and career guidance for PLC, automation, SAP, IT and data courses in Noida.',
    keywords: 'placement training Noida, industrial training placement, automation jobs Noida',
    path: '/placements',
  },
  '/contact': {
    title: 'Contact Bharat Training | Sector 3 Noida',
    description:
      'Visit Bharat Training at GF E-25, Sector-3 Noida, Opp. Shiv Mandir, near Sector-16 Metro. Call +91 88518 68521 or +91 82383 75399.',
    keywords: 'Bharat Training contact, industrial training Noida address, Sector 3 Noida training center',
    path: '/contact',
  },
  '/admission': {
    title: 'Apply Free for Training Courses in Noida | Bharat Training',
    description:
      'Apply for free counselling at Bharat Industrial Training. Admissions open for PLC, SCADA, automation, SAP, IT, data and digital marketing courses in Noida.',
    keywords: 'admission industrial training Noida, apply PLC course, free counselling Bharat Training',
    path: '/admission',
  },
};

function normalizePath(pathname) {
  if (!pathname || pathname === '/') return '/';
  return pathname.replace(/\/+$/, '') || '/';
}

function absoluteUrl(path) {
  if (!path) return SITE_URL;
  return path.startsWith('http') ? path : `${SITE_URL}${path}`;
}

function canonicalForPath(path) {
  return `${SITE_URL}${path === '/' ? '' : path}`;
}

function buildWebPageSchema(page) {
  const canonical = canonicalForPath(page.path);
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${canonical}#webpage`,
    url: canonical,
    name: page.title,
    description: page.description,
    inLanguage: 'en-IN',
    isPartOf: { '@id': `${SITE_URL}/#website` },
    about: { '@id': `${SITE_URL}/#organization` },
  };
}

function buildBreadcrumbSchema(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: canonicalForPath(item.path),
    })),
  };
}

function buildPageSchemas(page) {
  const items = [{ name: 'Home', path: '/' }];
  if (page.path !== '/') items.push({ name: page.title.split('|')[0].trim(), path: page.path });

  const schemas = [buildWebPageSchema(page), buildBreadcrumbSchema(items)];
  if (page.path === '/') schemas.push(WEBSITE_SCHEMA);
  if (page.path === '/courses') schemas.push(COURSE_CATALOG_SCHEMA);
  if (page.path === '/contact') schemas.push(CONTACT_PAGE_SCHEMA);
  return schemas;
}

function parsePrice(price) {
  if (!price) return null;
  const numeric = String(price).replace(/[^\d.]/g, '');
  return numeric || null;
}

function buildCourseSchema(course) {
  const url = canonicalForPath(getCoursePath(course));
  const price = parsePrice(course.price);

  return {
    '@context': 'https://schema.org',
    '@type': 'Course',
    '@id': `${url}#course`,
    name: course.title,
    description: course.seoDescription,
    url,
    image: absoluteUrl(course.img),
    inLanguage: 'en-IN',
    provider: { '@id': `${SITE_URL}/#organization` },
    educationalCredentialAwarded: 'Certificate',
    courseMode: 'Blended',
    about: course.category,
    teaches: course.features,
    hasCourseInstance: {
      '@type': 'CourseInstance',
      name: `${course.title} classroom training in Noida`,
      courseMode: 'Onsite',
      location: PLACE,
    },
    ...(price
      ? {
          offers: {
            '@type': 'Offer',
            url,
            price,
            priceCurrency: 'INR',
            availability: 'https://schema.org/InStock',
            category: 'Education',
          },
        }
      : {}),
  };
}

function buildFaqSchema(course) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: getCourseFaqs(course).map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

function buildCoursePageSchemas(course) {
  const path = getCoursePath(course);
  const page = {
    title: course.seoTitle,
    description: course.seoDescription,
    path,
  };

  return [
    buildWebPageSchema(page),
    buildBreadcrumbSchema([
      { name: 'Home', path: '/' },
      { name: 'Courses', path: '/courses' },
      { name: course.title, path },
    ]),
    buildCourseSchema(course),
    buildFaqSchema(course),
  ];
}

export function getSEOForPath(pathname) {
  const path = normalizePath(pathname);

  if (PAGE_SEO[path]) {
    const page = PAGE_SEO[path];
    return { ...page, jsonLd: buildPageSchemas(page) };
  }

  const courseMatch = path.match(/^\/courses\/([^/]+)$/);
  if (courseMatch) {
    const course = getCourseBySlug(courseMatch[1]);
    if (course) {
      return {
        title: course.seoTitle,
        description: course.seoDescription,
        keywords: course.keywords,
        path: getCoursePath(course),
        jsonLd: buildCoursePageSchemas(course),
      };
    }
  }

  return {
    title: 'Page Not Found | Bharat Training',
    description: 'The page you are looking for does not exist. Explore our PLC, SCADA and industrial training courses in Noida.',
    keywords: DEFAULT_KEYWORDS,
    path,
    noindex: true,
  };
}

export const ORGANIZATION_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': ['EducationalOrganization', 'LocalBusiness'],
  '@id': `${SITE_URL}/#organization`,
  name: SITE_NAME,
  alternateName: ['Bharat Training', 'Bharat Industrial Training Noida'],
  url: SITE_URL,
  logo: `${SITE_URL}/logo.jpg`,
  image: `${SITE_URL}/og-image.png`,
  telephone: ['+918851868521', '+918238375399'],
  email: ['hr@bharattraining.com', 'info@bharattraining.com'],
  address: ADDRESS,
  geo: PLACE.geo,
  priceRange: 'Rs 8,999 - Rs 27,999',
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
      opens: '09:00',
      closes: '19:00',
    },
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: 'Sunday',
      opens: '10:00',
      closes: '16:00',
    },
  ],
  contactPoint: [
    {
      '@type': 'ContactPoint',
      telephone: '+918851868521',
      contactType: 'admissions',
      areaServed: 'IN',
      availableLanguage: ['en', 'hi'],
    },
  ],
  sameAs: [
    'https://www.youtube.com/@bharattraining-l8w',
    'https://www.instagram.com/hr.bharattraining0505',
    'https://www.linkedin.com/in/lata-joshi-332882203',
    'https://www.facebook.com/people/BI-T/pfbid0b52YJTMPAURvBtsL5FJT6SMJQmXSP89Cdr6pBdJwck9DS3LZPen7ZFwm2v2L5kRrl/',
  ],
  description:
    'Premier industrial training institute in Noida offering PLC, SCADA, industrial automation, SAP and IT courses with practical labs and placement support.',
  areaServed: { '@type': 'City', name: 'Noida' },
};

const WEBSITE_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${SITE_URL}/#website`,
  name: SITE_NAME,
  alternateName: 'Bharat Training',
  url: SITE_URL,
  inLanguage: 'en-IN',
  publisher: { '@id': `${SITE_URL}/#organization` },
};

const COURSE_CATALOG_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  '@id': `${SITE_URL}/courses#course-list`,
  name: 'Bharat Training course catalog',
  itemListElement: allCourses.map((course, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: course.title,
    url: canonicalForPath(getCoursePath(course)),
  })),
};

const CONTACT_PAGE_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  '@id': `${SITE_URL}/contact#contact-page`,
  url: `${SITE_URL}/contact`,
  name: 'Contact Bharat Industrial Training',
  about: { '@id': `${SITE_URL}/#organization` },
};
